import { useCallback } from 'react';
import { useHomeChat } from '../../context/HomeChatContext';
import { useToast } from '../../../../../components/toast';
import { deleteDocumentByTenant } from '../../../../../services/document';

const DEFAULT_ERROR_MESSAGE = 'Failed to delete document.';

/**
 * Hook que centraliza la lógica de borrado de documentos: API, sincronización de
 * OpenDocumentCard, consume de messageId y cierre del panel cuando corresponde.
 * Usado por DocumentPanel y ChatPanel.
 *
 * @returns {{ handleDeleteDocument: (doc: { id: string, documentUuid?: string, document_uuid?: string, messageId?: string, conversationId?: string }) => Promise<void> }}
 */
export function useDeleteDocument() {
  const {
    deleteDocument,
    removeDocumentCreatedMessageId,
    consumeLastOpenedFromMessageId,
    closeDocumentPanel,
    currentDocumentId,
    selectedConversationId,
  } = useHomeChat();
  const toast = useToast();

  const handleDeleteDocument = useCallback(
    async (doc) => {
      const id = doc?.id;
      if (!id) return;

      try {
        const docUuid = doc?.documentUuid ?? doc?.document_uuid;
        if (docUuid) {
          await deleteDocumentByTenant({ document_uuid: docUuid });
        }

        const messageId = doc?.messageId;
        const conversationId = doc?.conversationId ?? selectedConversationId;

        if (currentDocumentId === id) {
          consumeLastOpenedFromMessageId();
        }
        if (messageId && conversationId) {
          removeDocumentCreatedMessageId(conversationId, messageId);
        }

        deleteDocument(id);
        if (currentDocumentId === id) {
          closeDocumentPanel();
        }

        toast.success({ message: 'Document deleted successfully.' });
      } catch (err) {
        const message =
          err?.response?.data?.msgCode?.[0]?.description ??
          err?.message ??
          DEFAULT_ERROR_MESSAGE;
        toast.error({ message });
        throw err;
      }
    },
    [
      deleteDocument,
      removeDocumentCreatedMessageId,
      consumeLastOpenedFromMessageId,
      closeDocumentPanel,
      currentDocumentId,
      selectedConversationId,
      toast,
    ]
  );

  return { handleDeleteDocument };
}
